import { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Check, ChevronDown, ChevronUp, CornerUpLeft } from "lucide-react";
import { chaptersFor, chapterForScreen, progressFraction } from "../chapters";
import { DEFAULT_BAND, type Band } from "../band";
import type { AssessmentV2State } from "../types";
import { cn } from "@/lib/utils";

/**
 * The journey dock — ported from v1's JourneyProgressBar.
 *
 * A single line at the bottom that answers the only two questions a learner
 * has mid-walk: "what's coming next?" and "how do I go back?". Tapping it
 * expands the whole journey — chapters done, the one you're in, what's left.
 *
 * Presentation only: chapters, order and progress all come from the canonical
 * walk (chapters.ts). Nothing here is scored or stored.
 */

/** Rough minutes per chapter, derived from how many decisions it holds. */
function etaMins(screenCount: number): number {
  return Math.max(1, Math.round(screenCount * 0.35));
}

interface Props {
  screenId: string;
  band?: Band;
  state?: AssessmentV2State;
  onBack?: () => void;
  canGoBack?: boolean;
}

export function JourneyDock({
  screenId,
  band = DEFAULT_BAND,
  state,
  onBack,
  canGoBack = true,
}: Props) {
  const [open, setOpen] = useState(false);
  const reduce = useReducedMotion();

  const chapters = chaptersFor(band);
  const current = chapterForScreen(screenId, band);
  const currentIdx = Math.max(0, chapters.findIndex((c) => c.id === current?.id));
  const next = chapters[currentIdx + 1];
  const upNext = next ?? chapters[currentIdx];
  const frac = progressFraction(screenId, state);

  if (!upNext) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 pointer-events-none">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-background/60 backdrop-blur-sm pointer-events-auto"
            onClick={() => setOpen(false)}
            aria-hidden
          />
        )}
      </AnimatePresence>

      {onBack && canGoBack && !open && (
        <div className="relative flex justify-center pb-3 pointer-events-none">
          <button
            onClick={onBack}
            className="pointer-events-auto inline-flex min-h-11 items-center gap-2 rounded-full
              border border-border/60 bg-background/70 backdrop-blur px-4 text-sm
              text-muted-foreground hover:text-foreground transition-colors
              focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <CornerUpLeft className="h-4 w-4" aria-hidden /> Go back
          </button>
        </div>
      )}

      <motion.div
        layout={!reduce}
        className="relative pointer-events-auto bg-background/95 backdrop-blur-xl border-t border-border/60"
      >
        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              key="expanded"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: reduce ? 0 : 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="overflow-hidden"
            >
              <div className="max-w-2xl mx-auto px-6 py-7 max-h-[60dvh] overflow-y-auto">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-5">
                  Your journey
                </p>
                <ol className="relative">
                  {chapters.map((c, i) => {
                    const done = i < currentIdx;
                    const active = i === currentIdx;
                    const isLast = i === chapters.length - 1;
                    return (
                      <li key={c.id} className="relative pl-9 pb-6 last:pb-0">
                        {!isLast && (
                          <span
                            className={cn(
                              "absolute left-[10px] top-6 bottom-0 w-px",
                              done ? "bg-primary" : "bg-border",
                            )}
                            aria-hidden
                          />
                        )}
                        <span
                          className={cn(
                            "absolute left-0 top-0.5 h-5 w-5 rounded-full grid place-items-center border-2",
                            done
                              ? "bg-primary border-primary text-primary-foreground"
                              : active
                                ? "bg-background border-foreground"
                                : "bg-background border-border",
                          )}
                          aria-hidden
                        >
                          {done && <Check className="h-3 w-3" strokeWidth={3} />}
                        </span>
                        <div>
                          <p
                            className={cn(
                              "text-sm font-display font-medium",
                              active ? "text-foreground" : "text-muted-foreground",
                            )}
                          >
                            {c.title}
                            {active && (
                              <span className="ml-2 text-[10px] uppercase tracking-widest text-primary">
                                You're here
                              </span>
                            )}
                          </p>
                          {!done && (
                            <p className="text-[10px] uppercase tracking-wider text-primary mt-0.5">
                              ~ {etaMins(c.screens.length)}{" "}
                              {etaMins(c.screens.length) === 1 ? "min" : "mins"}
                            </p>
                          )}
                          {c.blurb && (
                            <p className="mt-1.5 text-sm text-foreground/70">{c.blurb}</p>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <button
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-4 px-5 sm:px-8 py-3 min-h-11 text-left
            hover:bg-secondary/40 transition-colors
            focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          aria-expanded={open}
          aria-label={open ? "Hide your journey" : "Show your journey"}
        >
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
              {next ? "Up next" : "Final chapter"}
            </p>
            <p className="text-sm font-semibold text-foreground truncate">{upNext.title}</p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-[10px] uppercase tracking-wider text-primary">
              ~ {etaMins(upNext.screens.length)}{" "}
              {etaMins(upNext.screens.length) === 1 ? "min" : "mins"}
            </span>
            {open ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden />
            ) : (
              <ChevronUp className="h-4 w-4 text-muted-foreground" aria-hidden />
            )}
          </div>
        </button>

        <div className="h-1 w-full bg-secondary">
          <motion.div
            className="h-full bg-primary"
            initial={false}
            animate={{ width: `${Math.round(frac * 100)}%` }}
            transition={{ type: "spring", stiffness: 180, damping: 26 }}
          />
        </div>
      </motion.div>
    </div>
  );
}
